import React, { useEffect, useState } from "react";
import { Linkedin, Mail, Instagram, MessageCircle } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { api, getMl, fileUrl } from "@/lib/api";

const HEADERS = {
  en: { eyebrow: "Leadership", title: "The People Behind Nakia", subtitle: "Seasoned trade professionals steering our global operations across Indonesia, Türkiye, Singapore and beyond." },
  tr: { eyebrow: "Liderlik", title: "Nakia'nın Arkasındaki İnsanlar", subtitle: "Endonezya, Türkiye, Singapur ve ötesindeki küresel operasyonlarımızı yöneten deneyimli ticaret profesyonelleri." },
  id: { eyebrow: "Kepemimpinan", title: "Orang-Orang di Balik Nakia", subtitle: "Profesional perdagangan berpengalaman yang mengarahkan operasi global kami di Indonesia, Türkiye, Singapura dan lebih luas lagi." },
};

const Leadership = () => {
  const { lang } = useI18n();
  const [team, setTeam] = useState([]);
  const H = HEADERS[lang] || HEADERS.en;

  useEffect(() => {
    api.get("/team").then((r) => setTeam(r.data));
  }, []);

  if (team.length === 0) return null;

  return (
    <section className="py-24 sm:py-32 bg-[#0A1128] text-white relative overflow-hidden" data-testid="leadership-section">
      <div className="absolute inset-0 grain-overlay opacity-10 pointer-events-none" />
      <div className="container-nakia relative">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="h-px w-10 bg-[#D4AF37]" />
            <span className="text-eyebrow text-[#D4AF37]">{H.eyebrow}</span>
            <span className="h-px w-10 bg-[#D4AF37]" />
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight mb-4">{H.title}</h2>
          <p className="text-white/70">{H.subtitle}</p>
        </div>

        <div className={`grid gap-8 ${
          team.length === 1 ? "grid-cols-1 max-w-md mx-auto" :
          team.length === 2 ? "grid-cols-1 sm:grid-cols-2 max-w-3xl mx-auto" :
          "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
        }`}>
          {team.map((m) => {
            const socials = m.social_links || {};
            return (
              <div
                key={m.id}
                data-testid={`team-card-${m.id}`}
                className="group bg-[#161E32] border border-white/5 hover:border-[#D4AF37]/40 transition-all duration-500 hover:-translate-y-1 rounded-sm overflow-hidden"
              >
                {/* Portrait */}
                <div className="aspect-[4/5] bg-gradient-to-br from-[#1F2A44] to-[#0A1128] relative overflow-hidden">
                  {m.photo_url ? (
                    <img
                      src={fileUrl(m.photo_url)}
                      alt={m.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="font-display text-6xl text-[#D4AF37]/40">
                        {m.name?.split(" ").map((s) => s[0]).slice(0, 2).join("")}
                      </span>
                    </div>
                  )}
                  {/* Gradient overlay for text readability */}
                  <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-[#0A1128] via-[#0A1128]/60 to-transparent" />
                </div>
                {/* Meta */}
                <div className="p-7 relative">
                  <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] mb-2">
                    {getMl(m.position, lang)}
                  </div>
                  <h3 className="font-display text-2xl leading-tight mb-3">{m.name}</h3>
                  {getMl(m.bio, lang) && (
                    <p className="text-sm text-white/65 leading-relaxed line-clamp-4 mb-5">
                      {getMl(m.bio, lang)}
                    </p>
                  )}
                  <div className="flex items-center gap-2 pt-4 border-t border-white/5">
                    {socials.linkedin && (
                      <a href={socials.linkedin} target="_blank" rel="noopener noreferrer" data-testid={`team-linkedin-${m.id}`} aria-label="LinkedIn" className="w-9 h-9 flex items-center justify-center border border-white/10 hover:border-[#D4AF37] hover:text-[#D4AF37] text-white/70 rounded-sm transition-colors">
                        <Linkedin size={14} />
                      </a>
                    )}
                    {m.email && (
                      <a href={`mailto:${m.email}`} data-testid={`team-email-${m.id}`} aria-label="Email" className="w-9 h-9 flex items-center justify-center border border-white/10 hover:border-[#D4AF37] hover:text-[#D4AF37] text-white/70 rounded-sm transition-colors">
                        <Mail size={14} />
                      </a>
                    )}
                    {socials.instagram && (
                      <a href={socials.instagram} target="_blank" rel="noopener noreferrer" data-testid={`team-instagram-${m.id}`} aria-label="Instagram" className="w-9 h-9 flex items-center justify-center border border-white/10 hover:border-[#D4AF37] hover:text-[#D4AF37] text-white/70 rounded-sm transition-colors">
                        <Instagram size={14} />
                      </a>
                    )}
                    {socials.whatsapp && (
                      <a href={`https://wa.me/${socials.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" data-testid={`team-whatsapp-${m.id}`} aria-label="WhatsApp" className="w-9 h-9 flex items-center justify-center border border-white/10 hover:border-[#D4AF37] hover:text-[#D4AF37] text-white/70 rounded-sm transition-colors">
                        <MessageCircle size={14} />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Leadership;
